import { useState, useEffect, useRef, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { LogIn, LogOut, Camera, MapPin, Shield, User, Clock, ArrowLeft, Lock, Maximize } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiBase } from '../../lib/api';

const API = getApiBase();

export default function BadgeStationPage() {
  const [employees, setEmployees] = useState([]);
  const [selected, setSelected] = useState(null);
  const [pin, setPin] = useState('');
  const [step, setStep] = useState('list'); // list, pin, change-pin, badge, camera
  const [authData, setAuthData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selfieData, setSelfieData] = useState('');
  const [location, setLocation] = useState(null);
  const [geoStatus, setGeoStatus] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [currentTime, setCurrentTime] = useState(new Date());
  const [isKiosk, setIsKiosk] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    axios.get(`${API}/api/employees/badge-list`).then(r => setEmployees(r.data)).catch(() => {});
    axios.get(`${API}/api/settings`).then(r => setSettings(r.data)).catch(() => {});
  }, []);

  const enterKiosk = () => {
    const el = document.documentElement;
    if (el.requestFullscreen) el.requestFullscreen();
    else if (el.webkitRequestFullscreen) el.webkitRequestFullscreen();
    setIsKiosk(true);
  };

  const exitKiosk = () => {
    if (document.exitFullscreen) document.exitFullscreen();
    else if (document.webkitExitFullscreen) document.webkitExitFullscreen();
    setIsKiosk(false);
  };

  useEffect(() => {
    const onChange = () => setIsKiosk(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const reset = () => {
    setSelected(null); setPin(''); setStep('list'); setAuthData(null);
    setSelfieData(''); setLocation(null); setGeoStatus(''); setNewPin(''); setConfirmPin('');
    stopCamera();
  };

  const requiresSelfie = settings?.badge_require_selfie !== false;
  const requiresGeolocation = settings?.badge_require_geolocation !== false;

  const handleSelectEmployee = (emp) => { setSelected(emp); setPin(''); setStep('pin'); };

  const handlePinSubmit = async () => {
    if (pin.length !== 4) { toast.error('Entrez un code a 4 chiffres'); return; }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/api/employees/badge/pin-auth`, { employee_id: selected.id, pin });
      setAuthData(data);
      if (data.must_change_pin) { setStep('change-pin'); }
      else { setStep('badge'); }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Code PIN incorrect');
    } finally { setLoading(false); }
  };

  const handlePinChange = async () => {
    if (newPin.length !== 4 || !newPin.match(/^\d{4}$/)) { toast.error('Le PIN doit contenir 4 chiffres'); return; }
    if (newPin !== confirmPin) { toast.error('Les codes ne correspondent pas'); return; }
    if (newPin === '0000') { toast.error('Choisissez un code different de 0000'); return; }
    setLoading(true);
    try {
      await axios.post(`${API}/api/employees/badge/pin-change`, { employee_id: selected.id, old_pin: pin, new_pin: newPin });
      toast.success('Code PIN modifie !');
      setPin(newPin);
      setStep('badge');
      setAuthData(prev => ({ ...prev, must_change_pin: false }));
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
    finally { setLoading(false); }
  };

  const getLocation = () => new Promise((resolve, reject) => {
    setGeoStatus('Localisation...');
    if (!navigator.geolocation) { reject(new Error('GPS indisponible')); return; }
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeoStatus('OK'); resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }); },
      () => { setGeoStatus('Erreur GPS'); reject(new Error('GPS refuse')); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  });

  const performBadge = async (action, payload) => {
    const { data } = await axios.post(`${API}/api/employees/badge/${action}`, {
      employee_id: selected.id,
      pin,
      selfie: payload.selfie || '',
      latitude: payload.latitude || 0,
      longitude: payload.longitude || 0,
    });
    if (action === 'pin-out') toast.success(`Depart ! ${data.hours_worked}h travaillees`);
    else toast.success('Arrivee enregistree !');
    setTimeout(reset, 2000);
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'user', width: 320, height: 240 } });
      streamRef.current = stream;
      if (videoRef.current) videoRef.current.srcObject = stream;
    } catch { toast.error('Camera inaccessible'); setStep('badge'); }
  };

  const capturePhoto = () => {
    if (!videoRef.current) return;
    const c = document.createElement('canvas');
    c.width = 320; c.height = 240;
    c.getContext('2d').drawImage(videoRef.current, 0, 0, 320, 240);
    setSelfieData(c.toDataURL('image/jpeg', 0.5));
    stopCamera();
  };

  const stopCamera = () => {
    if (streamRef.current) { streamRef.current.getTracks().forEach(t => t.stop()); streamRef.current = null; }
  };

  const initBadge = async () => {
    setSelfieData(''); setGeoStatus('');
    let loc = null;
    if (!requiresSelfie && !requiresGeolocation) {
      setLoading(true);
      try {
        const action = authData.is_clocked_in ? 'pin-out' : 'pin-in';
        await performBadge(action, { selfie: '', latitude: 0, longitude: 0 });
      } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); setStep('badge'); }
      finally { setLoading(false); }
      return;
    }

    if (requiresGeolocation) {
      try {
        loc = await getLocation();
        setLocation(loc);
      } catch { toast.error('Activez la geolocalisation'); setStep('badge'); return; }
    }

    if (!requiresSelfie) {
      setLoading(true);
      try {
        const action = authData.is_clocked_in ? 'pin-out' : 'pin-in';
        await performBadge(action, { selfie: '', latitude: loc?.lat || 0, longitude: loc?.lng || 0 });
      } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); setStep('badge'); }
      finally { setLoading(false); }
      return;
    }

    setStep('camera');
    try {
      if (!requiresGeolocation) {
        await startCamera();
        return;
      }
      await startCamera();
    } catch {
      toast.error('Camera inaccessible');
      setStep('badge');
    }
  };

  const confirmBadge = async () => {
    if (requiresSelfie && !selfieData) { toast.error('Prenez une photo'); return; }
    setLoading(true);
    stopCamera();
    const action = authData.is_clocked_in ? 'pin-out' : 'pin-in';
    try {
      await performBadge(action, {
        selfie: requiresSelfie ? selfieData : '',
        latitude: requiresGeolocation ? (location?.lat || 0) : 0,
        longitude: requiresGeolocation ? (location?.lng || 0) : 0,
      });
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); setStep('badge'); }
    finally { setLoading(false); }
  };

  useEffect(() => () => stopCamera(), []);

  return (
    <div className="min-h-screen bg-[#1A2332]" data-testid="badge-station-page">
      {/* Header */}
      <div className="bg-[#1A2332] border-b border-white/10 px-4 py-4 flex items-center justify-between">
        <div className="text-center flex-1">
          <h1 className="font-heading text-xl font-bold text-white">Le Traiteur Libanais</h1>
          <p className="text-[#D4A840] text-sm mt-1">Pointage du personnel</p>
        </div>
        {!isKiosk ? (
          <button onClick={enterKiosk} className="text-xs text-white/40 hover:text-white/70 border border-white/20 rounded-lg px-3 py-1.5 flex items-center gap-1 transition-colors" data-testid="kiosk-enter">
            <Maximize className="w-3.5 h-3.5" /> Mode Kiosk
          </button>
        ) : (
          <button onClick={exitKiosk} className="text-xs text-white/40 hover:text-white/70 border border-white/20 rounded-lg px-3 py-1.5" data-testid="kiosk-exit">
            Quitter
          </button>
        )}
      </div>

      {/* Clock */}
      <div className="text-center py-6">
        <p className="font-heading text-5xl font-bold text-white tracking-tight">
          {currentTime.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })}
        </p>
        <p className="text-sm text-white/50 mt-1">
          {currentTime.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })}
        </p>
      </div>

      {step === 'list' && (
        <div className="max-w-2xl mx-auto px-4 pb-8">
          <p className="text-white/60 text-sm text-center mb-4">Selectionnez votre nom</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {employees.map(emp => (
              <button key={emp.id} onClick={() => handleSelectEmployee(emp)}
                className="bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#D4A840]/50 rounded-xl p-4 text-center transition-all"
                data-testid={`emp-select-${emp.id}`}>
                <div className="w-12 h-12 rounded-full bg-[#D4A840]/20 flex items-center justify-center mx-auto mb-2">
                  <User className="w-6 h-6 text-[#D4A840]" />
                </div>
                <p className="text-white font-medium text-sm">{emp.first_name}</p>
                <p className="text-white/50 text-xs">{emp.last_name}</p>
              </button>
            ))}
          </div>
          {employees.length === 0 && <p className="text-center text-white/40 py-12">Aucun employe enregistre</p>}
        </div>
      )}

      {step === 'pin' && selected && (
        <div className="max-w-sm mx-auto px-4 text-center">
          <button onClick={reset} className="text-white/50 text-sm flex items-center gap-1 mb-6 mx-auto"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <div className="w-16 h-16 rounded-full bg-[#D4A840]/20 flex items-center justify-center mx-auto mb-3">
            <User className="w-8 h-8 text-[#D4A840]" />
          </div>
          <h2 className="text-white font-heading text-xl font-semibold mb-1">{selected.first_name} {selected.last_name}</h2>
          <p className="text-white/50 text-sm mb-6">Entrez votre code PIN</p>
          <div className="flex justify-center gap-3 mb-6">
            {[0,1,2,3].map(i => (
              <div key={i} className={`w-14 h-14 rounded-xl border-2 flex items-center justify-center text-2xl font-bold ${pin.length > i ? 'border-[#D4A840] bg-[#D4A840]/10 text-[#D4A840]' : 'border-white/20 text-white/20'}`}>
                {pin.length > i ? '*' : ''}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-2 max-w-[240px] mx-auto mb-4">
            {[1,2,3,4,5,6,7,8,9,'',0,'del'].map((n, i) => (
              n === '' ? <div key={i} /> :
              <button key={i} onClick={() => {
                if (n === 'del') setPin(p => p.slice(0, -1));
                else if (pin.length < 4) setPin(p => p + n);
              }}
                className="h-12 rounded-xl bg-white/10 hover:bg-white/20 text-white text-lg font-medium transition-colors"
                data-testid={`pin-key-${n}`}>
                {n === 'del' ? <ArrowLeft className="w-5 h-5 mx-auto" /> : n}
              </button>
            ))}
          </div>
          <Button onClick={handlePinSubmit} disabled={pin.length !== 4 || loading}
            className="w-full rounded-xl bg-[#D4A840] hover:bg-[#c9a030] text-[#1A2332] h-12 font-semibold" data-testid="pin-submit">
            <Lock className="w-4 h-4 mr-2" /> {loading ? '...' : 'Valider'}
          </Button>
        </div>
      )}

      {step === 'change-pin' && (
        <div className="max-w-sm mx-auto px-4 text-center">
          <Shield className="w-12 h-12 text-[#D4A840] mx-auto mb-4" />
          <h2 className="text-white font-heading text-xl font-semibold mb-2">Changez votre code PIN</h2>
          <p className="text-white/50 text-sm mb-6">Premiere connexion : choisissez un nouveau code a 4 chiffres</p>
          <div className="space-y-3 mb-6">
            <div><Label className="text-white/70 text-sm">Nouveau PIN</Label>
              <Input type="password" maxLength={4} className="mt-1 rounded-xl bg-white/10 border-white/20 text-white text-center text-xl tracking-[0.5em]" value={newPin} onChange={e => setNewPin(e.target.value.replace(/\D/g, ''))} data-testid="new-pin" />
            </div>
            <div><Label className="text-white/70 text-sm">Confirmer</Label>
              <Input type="password" maxLength={4} className="mt-1 rounded-xl bg-white/10 border-white/20 text-white text-center text-xl tracking-[0.5em]" value={confirmPin} onChange={e => setConfirmPin(e.target.value.replace(/\D/g, ''))} data-testid="confirm-pin" />
            </div>
          </div>
          <Button onClick={handlePinChange} disabled={loading} className="w-full rounded-xl bg-[#D4A840] hover:bg-[#c9a030] text-[#1A2332] h-12 font-semibold" data-testid="change-pin-submit">
            {loading ? '...' : 'Enregistrer'}
          </Button>
        </div>
      )}

      {step === 'badge' && authData && (
        <div className="max-w-sm mx-auto px-4 text-center">
          <button onClick={reset} className="text-white/50 text-sm flex items-center gap-1 mb-6 mx-auto"><ArrowLeft className="w-4 h-4" /> Retour</button>
          <h2 className="text-white font-heading text-xl font-semibold mb-1">{authData.employee.first_name} {authData.employee.last_name}</h2>
          <Badge className={`text-sm px-4 py-1 mb-8 ${authData.is_clocked_in ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'}`}>
            {authData.is_clocked_in ? 'En service' : 'Hors service'}
          </Badge>
          <div className="flex justify-center gap-3 text-xs text-white/40 mb-5 flex-wrap">
            <span className="flex items-center gap-1">
              <Camera className="w-3 h-3" /> {requiresSelfie ? 'Selfie requis' : 'Selfie optionnel'}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" /> {requiresGeolocation ? 'GPS requis' : 'GPS optionnel'}
            </span>
          </div>
          <div className="flex justify-center mb-6">
            <button onClick={initBadge} disabled={loading}
              className={`w-36 h-36 rounded-full flex flex-col items-center justify-center gap-2 text-white font-medium text-lg shadow-lg active:scale-95 transition-all ${
                authData.is_clocked_in ? 'bg-red-600 hover:bg-red-700 shadow-red-600/30' : 'bg-green-600 hover:bg-green-700 shadow-green-600/30'
              }`} data-testid="badge-action-button">
              {authData.is_clocked_in ? <LogOut className="w-8 h-8" /> : <LogIn className="w-8 h-8" />}
              {authData.is_clocked_in ? 'Depart' : 'Arrivee'}
            </button>
          </div>
          <div className="flex justify-center gap-3 text-xs text-white/40">
            <span className="flex items-center gap-1"><Camera className="w-3 h-3" /> Selfie</span>
            <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> GPS</span>
            <span className="flex items-center gap-1"><Shield className="w-3 h-3" /> Securise</span>
          </div>
        </div>
      )}

      {step === 'camera' && (
        <div className="max-w-sm mx-auto px-4">
          <div className="bg-white/5 rounded-xl p-4 border border-white/10">
            {requiresGeolocation && (
              <div className="flex items-center gap-2 text-sm text-white/60 mb-3">
                <MapPin className="w-4 h-4" /> <span className={location ? 'text-green-400' : 'text-[#D4A840]'}>{geoStatus || 'Localisation...'}</span>
              </div>
            )}
            {!selfieData ? (
              <>
                <div className="bg-black rounded-xl overflow-hidden aspect-[4/3] mb-3">
                  <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" style={{ transform: 'scaleX(-1)' }} />
                </div>
                <Button onClick={capturePhoto} className="w-full rounded-xl bg-[#D4A840] hover:bg-[#c9a030] text-[#1A2332]" data-testid="capture-selfie-station">
                  <Camera className="w-4 h-4 mr-2" /> Prendre la photo
                </Button>
              </>
            ) : (
              <>
                <div className="rounded-xl overflow-hidden border border-white/10 mb-3">
                  <img src={selfieData} alt="Selfie" className="w-full" style={{ transform: 'scaleX(-1)' }} />
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => { setSelfieData(''); startCamera(); }} className="flex-1 rounded-xl border-white/20 text-white hover:bg-white/10">Reprendre</Button>
                  <Button onClick={confirmBadge} disabled={loading} className="flex-1 rounded-xl bg-green-600 hover:bg-green-700 text-white" data-testid="confirm-badge-station">
                    {loading ? '...' : 'Confirmer'}
                  </Button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
